'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/shared/components/Button';
import VideoCarousel from '@/shared/components/VideoCarousel';
import { useAnalytics } from '@/core/analytics';

export default function TermsPage() {
	const analytics = useAnalytics();

	useEffect(() => {
		analytics.logEvent('terms_page_visited');
	}, []);

	return (
		<div className="min-h-screen overflow-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Navigation */}
			<nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/10">
				<div className="container mx-auto px-4 py-4 flex justify-between items-center">
					<Link href="/" className="flex items-center">
						<Image
							src="/ts-logo2-v2.png"
							alt="TabStudio Logo"
							width={160}
							height={53}
							className="h-12 w-auto"
							loading="lazy"
							unoptimized
						/>
					</Link>
					<div className="flex items-center gap-4">
						<Link href="/login" prefetch={false}>
							<Button
								variant="ghost"
								size="sm"
								className="glass-light glass-hover text-white border-white/20"
							>
								Sign In
							</Button>
						</Link>
						<Link href="/register" prefetch={false}>
							<Button
								variant="default"
								size="sm"
								className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
							>
								Get Started
							</Button>
						</Link>
					</div>
				</div>
			</nav>

			<main className="relative z-10 pt-24">
				{/* Hero Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto max-w-4xl">
						<h1 className="text-5xl md:text-7xl font-bold mb-6 text-center">
							<span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
								Terms of
							</span>{' '}
							Service
						</h1>
						<p className="text-xl text-muted-foreground text-center mb-8">
							Last Updated: 1 November 2025
						</p>
					</div>
				</section>

				{/* Content */}
				<section className="py-16 px-4">
					<div className="container mx-auto max-w-4xl">
						<div className="prose prose-invert max-w-none">
							<div className="space-y-8">
								{/* Introduction */}
								<div>
									<p className="text-muted-foreground leading-relaxed mb-4">
										These Terms of Service
										(&quot;Terms&quot;) govern access to and
										use of the websites, applications,
										tools, and services provided by TabSpace
										AI Pte. Ltd. and its affiliates,
										including TabSpace Technologies Sdn.
										Bhd. (collectively,
										&quot;TabStudio&quot;, &quot;we&quot;,
										&quot;our&quot;, or &quot;us&quot;). By
										creating an account, accessing, or using
										any TabStudio service (the
										&quot;Services&quot;), you
										(&quot;Customer&quot;, &quot;you&quot;,
										or &quot;your&quot;) agree to these
										Terms.
									</p>
									<p className="text-muted-foreground leading-relaxed">
										If you use the Services on behalf of a
										company or organisation, you represent
										that you are authorised to accept these
										Terms on its behalf and to bind it to
										these Terms.
									</p>
								</div>

								{/* Section 1 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										1. Who We Are; Contracting Entity
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										<strong>
											Controller / Service Provider:
										</strong>{' '}
										TabSpace AI Pte. Ltd., Singapore
										<br />
										<strong>
											Regional Processor / Sub-Processor:
										</strong>{' '}
										TabSpace Technologies Sdn. Bhd.,
										Malaysia
										<br />
										<strong>Contact</strong> (including
										legal notices and abuse reports):{' '}
										<a
											href="mailto:legal@tabspace.ai"
											className="text-primary hover:underline"
										>
											legal@tabspace.ai
										</a>
									</p>
									<p className="text-muted-foreground leading-relaxed">
										These Terms incorporate by reference our
										Privacy Policy (EU-first data residency,
										GDPR-aligned) and any order forms,
										enterprise agreements, or data
										processing agreements (&quot;DPA&quot;)
										executed between you and us.
									</p>
								</div>

								{/* Section 2 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										2. Eligibility; Accounts
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>2.1 Minimum Age.</strong> You
										must be at least 13 years old (or 16
										where required by EU/UK law).
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>2.2 Account Security.</strong>{' '}
										You are responsible for all activity
										under your account, maintaining the
										confidentiality of credentials, and
										promptly notifying us of any suspected
										compromise.
									</p>
									<p className="text-muted-foreground leading-relaxed">
										<strong>
											2.3 Organisation Accounts.
										</strong>{' '}
										If you register using a business email
										domain, your organisation may control
										and administer the account and content.
									</p>
								</div>

								{/* Section 3 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										3. Service Scope; Beta & Trials
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>3.1 Services.</strong> TabStudio
										provides AI-assisted creative tools,
										including video/image generation, asset
										management, collaboration, and related
										features.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>
											3.2 Beta/Experimental Features.
										</strong>{' '}
										Certain features may be identified as
										&quot;Beta,&quot; &quot;Labs,&quot; or
										&quot;Preview.&quot; They are provided
										AS IS for evaluation only and may be
										modified or discontinued at any time.
									</p>
									<p className="text-muted-foreground leading-relaxed">
										<strong>
											3.3 No Professional Advice.
										</strong>{' '}
										Outputs are machine-generated and may
										contain inaccuracies. We do not provide
										legal, medical, financial, or
										professional advice.
									</p>
								</div>

								{/* Section 4 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										4. Subscriptions, Credits, Payments,
										Taxes
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>4.1 Plans & Billing.</strong>{' '}
										Access may require a paid subscription
										and/or usage-based credits. Prices and
										features are as presented at checkout,
										on the order form, or in your admin
										console.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>4.2 Auto-Renewal.</strong>{' '}
										Subscriptions renew automatically for
										the then-current term unless cancelled
										per Section 14.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>4.3 Refunds.</strong> Except
										where required by law or expressly
										stated otherwise, fees are
										non-refundable. Unused credits expire
										per plan terms.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>4.4 Taxes.</strong> Fees are
										exclusive of all applicable taxes,
										duties, and levies; you are responsible
										for these.
									</p>
									<p className="text-muted-foreground leading-relaxed">
										<strong>4.5 Payment Processor.</strong>{' '}
										We may use third-party processors (e.g.,
										Stripe). Your use of their services may
										be subject to their terms.
									</p>
								</div>

								{/* Section 5 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										5. Your Content; Licences to TabStudio
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>
											5.1 &quot;Customer Content.&quot;
										</strong>{' '}
										Any data, text, images, audio, video,
										prompts, instructions, storyboards, and
										other materials you upload or provide to
										the Services.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>
											5.2 Ownership of Customer Content.
										</strong>{' '}
										As between you and TabStudio, you retain
										all rights, title, and interest in and
										to your Customer Content, subject to the
										licences granted in these Terms.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>
											5.3 Operational Licence to
											TabStudio.
										</strong>{' '}
										You grant TabStudio a worldwide,
										non-exclusive, royalty-free licence to
										host, reproduce, process, adapt,
										transmit, and display Customer Content
										solely to provide and support the
										Services (including caching,
										transcoding, backup, content moderation,
										security, and product improvement based
										on aggregate/anonymous telemetry).
									</p>
									<p className="text-muted-foreground leading-relaxed">
										<strong>5.4 AI Training.</strong> By
										default, Customer Content is not used to
										train TabStudio&apos;s AI models. We
										will only use Customer Content for model
										training if you expressly opt-in (you
										may withdraw consent at any time). This
										does not restrict our use of aggregated
										or de-identified operational data for
										safety, reliability, or efficiency.
									</p>
								</div>

								{/* Section 6 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										6. AI Outputs; Co-Ownership (Joint IP)
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>
											6.1 &quot;Outputs.&quot;
										</strong>{' '}
										Content and materials generated or
										assisted by the Services in response to
										your inputs or configurations (e.g.,
										videos, images, text).
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>6.2 Platform IP.</strong>{' '}
										TabStudio retains all rights in the
										Services, models, algorithms, software,
										templates, UI/UX, and brand. No rights
										are granted except as expressly stated.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-3">
										<strong>
											6.3 Joint Ownership of Outputs (O3).
										</strong>{' '}
										Unless otherwise agreed in a signed
										enterprise addendum, you and TabStudio
										shall be co-owners of Outputs on a
										worldwide, perpetual, irrevocable,
										royalty-free basis. Each co-owner may
										use, reproduce, modify, distribute,
										publicly perform/display, and licence
										Outputs without accounting to the other,
										provided that:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3 ml-4">
										<li>
											(a) you must ensure your use of
											Outputs complies with applicable law
											and Section 8 (Acceptable Use);
										</li>
										<li>
											(b) TabStudio shall not assert IP
											claims against you for your use of
											Outputs you generated;
										</li>
										<li>
											(c) neither party shall use Outputs
											to claim endorsement by the other or
											to misuse the other&apos;s
											trademarks;
										</li>
										<li>
											(d) this joint ownership does not
											include a licence to use the other
											party&apos;s pre-existing IP (logos,
											trademarks, or third-party rights
											embedded in Customer Content), which
											remain owned by their respective
											owners.
										</li>
									</ul>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>6.4 Enterprise Option.</strong>{' '}
										For customers requiring exclusive
										ownership of Outputs, TabStudio offers
										an Enterprise Addendum that can
										supersede Section 6.3 with &quot;User
										Owns Output; Limited Licence to
										TabStudio.&quot;
									</p>
									<p className="text-muted-foreground leading-relaxed mb-3">
										<strong>6.5 Moral Rights.</strong> To
										the maximum extent permitted by law,
										each party waives (or agrees not to
										assert) any moral rights in the Outputs
										as against the other party and their
										licensees.
									</p>
									<p className="text-muted-foreground leading-relaxed text-sm italic">
										Note: IP laws vary by jurisdiction. The
										contractual rules in Section 6.3 govern
										the relationship between you and
										TabStudio, regardless of local default
										rules for &quot;joint works.&quot;
									</p>
								</div>

								{/* Section 7 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										7. Feedback
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										If you provide suggestions, ideas, or
										feedback, you grant TabStudio a
										perpetual, irrevocable, worldwide,
										royalty-free licence to use and exploit
										such feedback without restriction or
										obligation.
									</p>
								</div>

								{/* Section 8 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										8. Acceptable Use (Strict)
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-3">
										You agree not to use the Services or
										Outputs to:
									</p>
									<ul className="list-disc list-inside text-muted-foreground space-y-2 mb-3">
										<li>
											Violate any law or third-party
											rights (IP, privacy, publicity,
											contractual, or confidentiality
											obligations).
										</li>
										<li>
											Generate or distribute illegal,
											harmful, fraudulent, deceptive,
											malware, or spam content.
										</li>
										<li>
											Engage in hate, harassment, threats,
											extremist or terrorist content, or
											incitement to violence.
										</li>
										<li>
											Generate sexually explicit content
											involving minors or
											exploitative/abusive material; or
											non-consensual intimate imagery
											(including deepfakes).
										</li>
										<li>
											Create deceptive or manipulative
											political persuasion, unlawfully
											interfere with civic processes, or
											impersonate others.
										</li>
										<li>
											Infringe biometric/likeness rights:
											any use of real persons&apos;
											faces/voices requires you to secure
											all necessary consents and comply
											with applicable laws.
										</li>
										<li>
											Bypass technical controls, rate
											limits, or attempt to
											reverse-engineer the Services.
										</li>
										<li>
											Benchmark or train separate models
											on Outputs where prohibited by law
											or an applicable order form.
										</li>
										<li>
											Interfere with or disrupt the
											Services, networks, or third-party
											integrations.
										</li>
									</ul>
									<p className="text-muted-foreground leading-relaxed">
										We may suspend or terminate accounts for
										violations and may block or remove
										content/Outputs that breach this
										Section.
									</p>
								</div>

								{/* Section 9 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										9. Third-Party Services; Open Source
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										The Services may interoperate with
										third-party products (e.g., payment
										processors, cloud providers, analytics).
										Your use of such products is subject to
										their terms and policies. Certain
										components may be provided under
										open-source licences; where applicable,
										those licences govern that code.
									</p>
								</div>

								{/* Section 10 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										10. Privacy; Data Protection
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										Our Privacy Policy explains how we
										collect and process personal data,
										including EU-first data residency,
										international transfers under SCCs and
										Art. 46 GDPR, and data subject rights.
										For enterprise processing, our DPA
										applies where executed. Contact:{' '}
										<a
											href="mailto:legal@tabspace.ai"
											className="text-primary hover:underline"
										>
											legal@tabspace.ai
										</a>
									</p>
								</div>

								{/* Section 11 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										11. Intellectual Property Notices;
										Takedowns (DMCA-style)
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										If you believe content or Outputs
										infringe your IP, notify us at{' '}
										<a
											href="mailto:legal@tabspace.ai"
											className="text-primary hover:underline"
										>
											legal@tabspace.ai
										</a>{' '}
										with: (a) identification of the
										copyrighted work or mark, (b)
										identification of the infringing
										material, (c) your contact information,
										and (d) a statement of good-faith belief
										and authority to act. We may remove or
										disable access and may terminate repeat
										infringers.
									</p>
								</div>

								{/* Section 12 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										12. Service Changes; Availability
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										We may enhance, modify, or discontinue
										features with advance notice where
										practicable. The Services may be
										unavailable from time to time due to
										maintenance, updates, or network
										incidents. No 100% uptime guarantee is
										provided unless expressly set in an SLA.
									</p>
								</div>

								{/* Section 13 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										13. Security
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										We implement industry-standard
										safeguards (encryption in transit/at
										rest, RBAC, auditing, incident
										response). You are responsible for
										securing your devices, networks, and any
										API keys or credentials issued to you.
									</p>
								</div>

								{/* Section 14 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										14. Term; Suspension; Termination
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>14.1 Term.</strong> These Terms
										commence on the earlier of account
										creation or first use and continue until
										terminated.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>14.2 Your Termination.</strong>{' '}
										You may terminate at any time by
										cancelling your plan and ceasing use.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>
											14.3 Our Suspension/Termination.
										</strong>{' '}
										We may suspend or terminate for: (a)
										material breach (including Section 8),
										(b) non-payment, (c) legal or safety
										reasons, or (d) upon discontinuation of
										the Services.
									</p>
									<p className="text-muted-foreground leading-relaxed">
										<strong>14.4 Effect.</strong> Upon
										termination, your right to access the
										Services ceases. Sections intended to
										survive (including 5–9, 11–19) will
										survive. We will handle Customer Content
										per the Privacy Policy and any DPA.
									</p>
								</div>

								{/* Section 15 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										15. Disclaimers
									</h2>
									<p className="text-muted-foreground leading-relaxed text-sm uppercase">
										THE SERVICES AND ALL OUTPUTS ARE
										PROVIDED &quot;AS IS&quot; AND &quot;AS
										AVAILABLE.&quot; TO THE MAXIMUM EXTENT
										PERMITTED BY LAW, WE DISCLAIM ALL
										WARRANTIES, EXPRESS OR IMPLIED,
										INCLUDING MERCHANTABILITY, FITNESS FOR A
										PARTICULAR PURPOSE, NON-INFRINGEMENT,
										AND ACCURACY OF OUTPUTS. YOU ACKNOWLEDGE
										THAT AI OUTPUTS MAY BE INACCURATE,
										INCOMPLETE, OFFENSIVE, OR OTHERWISE
										UNSUITED TO YOUR PURPOSES.
									</p>
								</div>

								{/* Section 16 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										16. Limitation of Liability
									</h2>
									<p className="text-muted-foreground leading-relaxed text-sm uppercase mb-2">
										TO THE MAXIMUM EXTENT PERMITTED BY LAW:
									</p>
									<p className="text-muted-foreground leading-relaxed text-sm uppercase mb-2">
										(a) NEITHER PARTY WILL BE LIABLE FOR
										INDIRECT, INCIDENTAL, SPECIAL,
										CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
										DAMAGES, OR LOST PROFITS, REVENUE,
										GOODWILL, OR DATA, EVEN IF ADVISED OF
										THE POSSIBILITY;
									</p>
									<p className="text-muted-foreground leading-relaxed text-sm uppercase mb-2">
										(b) EACH PARTY&apos;S AGGREGATE
										LIABILITY FOR ALL CLAIMS ARISING OUT OF
										OR RELATING TO THE SERVICES OR THESE
										TERMS SHALL NOT EXCEED THE AMOUNTS PAID
										OR PAYABLE BY YOU TO TABSTUDIO IN THE 12
										MONTHS PRECEDING THE EVENT GIVING RISE
										TO LIABILITY, OR USD 1,000, WHICHEVER IS
										GREATER;
									</p>
									<p className="text-muted-foreground leading-relaxed text-sm uppercase">
										(c) NOTHING EXCLUDES LIABILITY THAT
										CANNOT BE EXCLUDED BY LAW.
									</p>
								</div>

								{/* Section 17 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										17. Indemnity
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										You will defend, indemnify, and hold
										harmless TabStudio and its affiliates,
										officers, directors, employees, and
										agents from and against any claims,
										damages, liabilities, costs, and
										expenses (including reasonable legal
										fees) arising out of or related to: (a)
										your Customer Content; (b) your use of
										the Services or Outputs in violation of
										these Terms or law; (c) alleged
										infringement or misuse of third-party
										rights (including likeness/biometric
										rights) by your Content or your use of
										Outputs; or (d) your breach of Section
										8.
									</p>
								</div>

								{/* Section 18 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										18. Governing Law; Venue
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										These Terms and any dispute or claim
										(including non-contractual disputes or
										claims) arising out of or in connection
										with them are governed by the laws of
										Singapore, without regard to conflicts
										of law principles. The parties submit to
										the exclusive jurisdiction of the courts
										of Singapore. Each party may seek
										injunctive or equitable relief in any
										court of competent jurisdiction.
									</p>
								</div>

								{/* Section 19 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										19. Export Controls; Sanctions
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										You represent that you are not located
										in, under the control of, or a national
										or resident of any country or entity
										subject to embargoes or sanctions and
										will not use the Services for prohibited
										end uses.
									</p>
								</div>

								{/* Section 20 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										20. Force Majeure
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										Neither party is liable for delays or
										failures due to events beyond its
										reasonable control, including acts of
										God, labour disputes, internet or cloud
										provider failures, governmental actions,
										or cyber incidents.
									</p>
								</div>

								{/* Section 21 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										21. Changes to These Terms
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										We may update these Terms from time to
										time. Material changes will be notified
										via the Services or email. Your
										continued use after the effective date
										constitutes acceptance.
									</p>
								</div>

								{/* Section 22 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										22. Miscellaneous
									</h2>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>22.1 Entire Agreement.</strong>{' '}
										These Terms, the Privacy Policy, any
										applicable order forms, and any DPA
										constitute the entire agreement and
										supersede all prior understandings.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>22.2 Severability.</strong> If
										any provision is held invalid, the
										remainder remains in effect.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>22.3 Assignment.</strong> You
										may not assign or transfer these Terms
										without our prior written consent; we
										may assign to an affiliate or in
										connection with a merger, acquisition,
										or asset sale.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>22.4 No Waiver.</strong> A
										failure to enforce any provision is not
										a waiver.
									</p>
									<p className="text-muted-foreground leading-relaxed mb-2">
										<strong>22.5 Notices.</strong> Notices
										must be sent to{' '}
										<a
											href="mailto:legal@tabspace.ai"
											className="text-primary hover:underline"
										>
											legal@tabspace.ai
										</a>{' '}
										(and to your registered email for
										notices we send you).
									</p>
									<p className="text-muted-foreground leading-relaxed">
										<strong>22.6 Headings.</strong> Headings
										are for convenience only and do not
										affect interpretation.
									</p>
								</div>

								{/* Section 23 */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										23. Enterprise Terms
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										For enterprise customers, a signed order
										form and/or Enterprise Addendum
										(including DPA, security exhibits, SLAs,
										and custom IP clauses) may modify these
										Terms. In case of conflict, the signed
										agreement controls.
									</p>
								</div>

								{/* Contact */}
								<div>
									<h2 className="text-2xl font-bold mb-4">
										Contact
									</h2>
									<p className="text-muted-foreground leading-relaxed">
										<strong>TabStudio Legal</strong>
										<br />
										Email:{' '}
										<a
											href="mailto:legal@tabspace.ai"
											className="text-primary hover:underline"
										>
											legal@tabspace.ai
										</a>
									</p>
									<p className="text-muted-foreground leading-relaxed text-sm mt-4 text-center font-semibold">
										END OF TERMS
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* CTA Section */}
				<section className="py-16 px-4">
					<div className="container mx-auto">
						<div className="glass-gradient glass-hover relative max-w-4xl mx-auto text-center p-8 rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
							<div className="relative z-10">
								<h2 className="text-xl font-bold mb-6 text-center">
									Questions About Our Terms?
								</h2>
								<p className="text-sm text-muted-foreground mb-6 max-w-2xl mx-auto">
									If you have any questions about these Terms
									of Service, please contact our legal team.
								</p>
								<a href="mailto:legal@tabspace.ai">
									<Button
										variant="default"
										size="lg"
										className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
									>
										Contact Legal Team
									</Button>
								</a>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
				<div className="container mx-auto px-4 py-12">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
						<div>
							<h4 className="font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/features">Features</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/about">About</Link>
								</li>
								<li>
									<Link href="/careers">Careers</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/help">Help Center</Link>
								</li>
								<li>
									<Link href="/api">API</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Legal</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/privacy">Privacy</Link>
								</li>
								<li>
									<Link href="/terms">Terms</Link>
								</li>
								<li>
									<Link href="/security">Security</Link>
								</li>
							</ul>
						</div>
					</div>
					<div className="pt-8 border-t border-border/50 text-center text-sm text-muted-foreground">
						<p>
							&copy; 2025 TabStudio. Powered by AI. Built for
							creators.
						</p>
					</div>
				</div>
			</footer>
		</div>
	);
}
